/**
 * Error Boundary dla aplikacji
 * Obsługuje błędy nieoczekiwane i wyświetla komunikaty błędów
 */
import { Component } from 'react';
import { Button } from '../ui/Button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { useState } from 'react';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-secondary-600" />
                <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  Wystąpił błąd
                </h2>
              </div>
            </CardHeader>

            <CardContent className="text-center space-y-6">
              <div className="text-neutral-600 dark:text-neutral-400 space-y-2">
                <p>Przepraszamy, wystąpił nieoczekiwany błąd w aplikacji.</p>
                <p className="text-sm">Możesz spróbować odświeżyć stronę lub wrócić do strony głównej.</p>
                {this.state.error && (
                  <details className="text-left text-xs mt-4 p-2 bg-neutral-100 dark:bg-neutral-800 rounded">
                    <summary className="cursor-pointer text-neutral-700 dark:text-neutral-300">
                      Szczegóły błędu (kliknij, aby rozwinąć)
                    </summary>
                    <code className="block mt-2 text-secondary-600 dark:text-secondary-400">
                      {this.state.error.message}
                    </code>
                  </details>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.reload()}
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Odśwież stronę
                </Button>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => window.location.href = '/'}
                  className="flex-1"
                >
                  Strona główna
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC dla łatwiejszego użycia

export const withErrorBoundary = (Component) => {
  return (props) => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
};

// Hook dla komponentów funkcyjnych

export const useErrorBoundary = () => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setHasError(false);
    setError(null);
  };

  const errorHandler = (error) => {
    setHasError(true);
    setError(error);
  };

  return { hasError, error, reset, errorHandler };
};
